import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aideAPI } from '../services/api';
import './aide/Aide.css';
import { libelleEcran } from '../aide';

/**
 * ADMINISTRATION → AIDE (étape A3 de l'aide contextuelle) — réservé au super-administrateur
 * =========================================================================================
 * Protocole : doc/specs_techniques/aide_contextuelle.md § 6 · Maquette : aide_contextuelle_maquette.html, vue
 * « Administration → Aide ».
 *
 * Les manques signalés depuis le bouton « ? » des modules, du plus récent au plus ancien. Chaque signalement a
 * AUSSI été envoyé par courriel : la colonne « Courriel » dit s'il est parti — sinon pourquoi (panne bruyante).
 * « Où » rouvre le module sur l'écran signalé, l'aide ouverte. Suite : Nouveau · Traité (aide modifiée) · Sans
 * suite, avec une note. Supprimer passe par une confirmation stylée (jamais confirm()).
 *
 * v99 — « PROPOSER UNE AMÉLIORATION » (maquettes validées le 2026-10-29) : « Signalements » → Propositions, colonne
 * « Proposition » avec la NATURE en pastille (📘 Aide · 💡 Idée · ⚠️ Défaut), filtre « Toutes les natures » ; « Traité
 * (aide modifiée) » → Traité ; la réponse dit la suite selon la nature. Les signalements d'avant v99 sont des « Aide ».
 *
 * v96 — RÉPONDRE AU DEMANDEUR (maquettes admin_maquette.html, onglet Aide, et aide_contextuelle_maquette.html, validées
 * le 2026-10-28). Jamais automatique : le menu « Suite » enregistre dès qu'on le change et la note en quittant le champ,
 * un envoi automatique partirait sur une erreur de clic ou avant la note. « Répondre… » ouvre un récapitulatif (À, De,
 * Objet, le message prérempli et modifiable, signé du nom de qui répond) ; la colonne « Réponse au demandeur » garde la
 * dernière réponse : transmise le …, « modifiée depuis » + Renvoyer… si la suite ou la note a changé ensuite, non
 * transmise + la raison + Réessayer…. Compte supprimé ou désactivé : pas d'envoi (décidé le 2026-10-28), la raison en
 * rouge. Renommés : « Courriel » → « Transmis aux admins », « Note » → « Réponse au demandeur (facultative) ».
 */
const momentFr = (v) => (v ? new Date(v).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
    + ' ' + new Date(v).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }) : '');
const momentLong = (v) => (v ? momentFr(v).replace(' ', ' à ') : '');
const SUITES = [['nouveau', 'Nouveau'], ['traite', 'Traité'], ['sans_suite', 'Sans suite']];
const NATURES = { aide: ['📘', 'Aide'], idee: ['💡', 'Idée'], defaut: ['⚠️', 'Défaut'] };
// v99 : « traitée » se dit selon la nature de la proposition
const TRAITEE = { aide: "traitée : l'aide a été complétée", idee: 'traitée : votre proposition a été prise en compte', defaut: 'traitée : le défaut a été corrigé' };
const suiteReponse = (s) => (s.suite === 'traite' ? TRAITEE[s.nature] || TRAITEE.aide : 'sans suite');

/** Le message prérempli de « Répondre… » — modifiable dans la fenêtre avant l'envoi. */
const messageReponse = (s, note, auteur) => `Bonjour ${s.auteur_nom},\n\n`
    + `Le ${momentFr(s.cree_le).slice(0, 10)}, vous avez proposé une amélioration du module ${s.module_libelle}, `
    + `sur l'écran « ${libelleEcran(s.module_cle, s.ecran_cle)} » :\n« ${s.texte} »\n\n`
    + `Suite donnée : ${suiteReponse(s)}.${note ? '\n' + note : ''}\n\n`
    + `Merci pour votre proposition : elle aide à améliorer l'application.\n\n${auteur || "L'administrateur de l'application"}`;

/** La colonne « Réponse au demandeur ». */
function CelluleReponse({ s, onRepondre }) {
    if (s.suite === 'nouveau') return <span className="hint">—</span>;
    if (s.auteur_compte) {
        return <span className="aide-admin-ko"><i className="fa-solid fa-triangle-exclamation" /> Non transmise — compte {s.auteur_compte === 'supprime' ? 'supprimé' : 'désactivé'}</span>;
    }
    if (!s.reponse_etat) {
        return <button type="button" className="a-btn a-btn-primary a-btn-sm" onClick={onRepondre}><i className="fa-solid fa-reply" /> Répondre…</button>;
    }
    if (s.reponse_etat === 'non_transmise') {
        return (<>
            <span className="aide-admin-ko"><i className="fa-solid fa-triangle-exclamation" /> Non transmise — {s.reponse_detail || 'raison inconnue'}</span>
            <button type="button" className="aide-lien aide-admin-rep-lien" onClick={onRepondre}>Réessayer…</button>
        </>);
    }
    return (<>
        <span className="aide-admin-ok"><i className="fa-solid fa-circle-check" /> Réponse transmise le {momentLong(s.reponse_le)}</span>
        {s.reponse_par && <div className="hint">par {s.reponse_par}</div>}
        {s.reponse_modifiee && (<>
            <div className="aide-admin-rep-chg"><i className="fa-solid fa-pen" /> suite ou réponse modifiée depuis</div>
            <button type="button" className="aide-lien aide-admin-rep-lien" onClick={onRepondre}>Renvoyer…</button>
        </>)}
    </>);
}

function AdminAide({ onMessage, onErreur, onCompteur, auteur }) {
    const navigate = useNavigate();
    const [donnees, setDonnees] = useState(null);
    const [filtres, setFiltres] = useState({ module: '', suite: '', nature: '' });
    const [notes, setNotes] = useState({});
    const [aSupprimer, setASupprimer] = useState(null);
    // v96 : la fenêtre « Répondre… » — { s, texte, etat: 'saisie' | 'envoi' | 'transmise' | 'non_transmise', detail }
    const [reponse, setReponse] = useState(null);
    // ⚠️ Les rappels du parent sont recréés à chaque rendu : dans les dépendances de `charger`, ils relanceraient la
    // lecture en boucle (fiche « rappel de parent hors dépendances »). On les lit par une référence.
    const rappels = useRef({ onMessage, onErreur, onCompteur });
    useEffect(() => { rappels.current = { onMessage, onErreur, onCompteur }; });

    const charger = useCallback(async () => {
        try {
            const r = await aideAPI.signalements({ module: filtres.module || undefined, suite: filtres.suite || undefined, nature: filtres.nature || undefined });
            setDonnees(r.data);
            setNotes(Object.fromEntries(r.data.signalements.map((s) => [s.id, s.suite_note || ''])));
            if (rappels.current.onCompteur) rappels.current.onCompteur(r.data.nouveaux);
        } catch (e) { rappels.current.onErreur(e.response?.data?.error || 'Lecture des propositions impossible'); }
    }, [filtres]);
    useEffect(() => { charger(); }, [charger]);

    const donnerSuite = async (s, suite, note) => {
        try {
            await aideAPI.donnerSuite(s.id, { suite, note });
            onMessage('Suite enregistrée');
            charger();
        } catch (e) { onErreur(e.response?.data?.error || "La suite n'a pas pu être enregistrée"); }
    };
    const supprimer = async () => {
        const id = aSupprimer.id;
        setASupprimer(null);
        try {
            await aideAPI.supprimerSignalement(id);
            onMessage('Proposition supprimée');
            charger();
        } catch (e) { onErreur(e.response?.data?.error || 'Suppression impossible'); }
    };
    const voir = (s) => navigate('/' + s.module_cle, { state: { aideEcran: s.ecran_cle } });

    // v96 — « Répondre… » : une note encore en cours de saisie est d'abord enregistrée (le message la cite, et
    // l'empreinte de la réponse doit être celle de la suite réellement enregistrée).
    const ouvrirReponse = async (s) => {
        const note = notes[s.id] || '';
        if (note !== (s.suite_note || '')) await donnerSuite(s, s.suite, note);
        setReponse({ s, texte: messageReponse(s, note.trim(), auteur), etat: 'saisie', detail: null });
    };
    const envoyerReponse = async () => {
        const { s, texte } = reponse;
        setReponse((r) => ({ ...r, etat: 'envoi' }));
        try {
            const r = await aideAPI.repondreSignalement(s.id, { texte });
            setReponse((x) => ({ ...x, etat: r.data.reponse_etat, detail: r.data.reponse_detail }));
        } catch (e) {
            setReponse((x) => ({ ...x, etat: 'non_transmise', detail: e.response?.data?.error || "La réponse n'a pas pu être envoyée" }));
        }
        charger();
    };

    if (!donnees) return <div className="a-card"><i className="fa-solid fa-circle-notch fa-spin" /> Lecture des propositions…</div>;
    const liste = donnees.signalements;

    return (
        <div className="aide-admin">
            <div className="mail-band info" style={{ marginBottom: 12 }}>
                <i className="fa-solid fa-circle-info" />
                <div>Les propositions d'amélioration envoyées depuis le bouton <b>« ? »</b> des modules — sur l'aide, une idée pour le module ou un défaut. Chacune vous est aussi envoyée par courriel. Une fois la suite donnée, <b>« Répondre… »</b> prévient le demandeur. Onglet visible du seul super-administrateur.</div>
            </div>
            <div className="a-card">
                <div className="a-card-h">
                    <i className="fa-solid fa-lightbulb" /> Propositions
                    <select className="docs-select" style={{ marginLeft: 'auto' }} value={filtres.module} aria-label="Module"
                            onChange={(e) => setFiltres({ ...filtres, module: e.target.value })}>
                        <option value="">Tous les modules</option>
                        {donnees.modules.map((m) => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
                    </select>
                    <select className="docs-select" value={filtres.nature} aria-label="Nature" onChange={(e) => setFiltres({ ...filtres, nature: e.target.value })}>
                        <option value="">Toutes les natures</option>
                        {Object.entries(NATURES).map(([v, [ic, l]]) => <option key={v} value={v}>{ic} {l}</option>)}
                    </select>
                    <select className="docs-select" value={filtres.suite} aria-label="Suite" onChange={(e) => setFiltres({ ...filtres, suite: e.target.value })}>
                        <option value="">Toutes les suites</option>
                        {SUITES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <span className="hint aide-admin-total">{liste.length} proposition{liste.length > 1 ? 's' : ''} · {donnees.nouveaux} nouveau{donnees.nouveaux > 1 ? 'x' : ''}</span>
                </div>
                <table className="tbl aide-admin-tbl">
                    <thead><tr><th style={{ width: 110 }}>Reçu le</th><th style={{ width: 150 }}>Par</th><th style={{ width: 200 }}>Où</th><th>Proposition</th><th style={{ width: 130 }}>Transmis aux admins</th><th style={{ width: 200 }}>Suite</th><th style={{ width: 190 }}>Réponse au demandeur</th><th style={{ width: 36 }} /></tr></thead>
                    <tbody>
                        {liste.length === 0 && <tr><td colSpan={8} className="hint" style={{ textAlign: 'center' }}>Aucune proposition.</td></tr>}
                        {liste.map((s) => (
                            <tr key={s.id} className={s.suite === 'nouveau' ? 'aide-admin-nouveau' : ''} data-id={s.id}>
                                <td>{momentFr(s.cree_le)}</td>
                                <td><b>{s.auteur_nom || '—'}</b><div className="hint">{s.departement_code}</div></td>
                                <td>{s.module_libelle}<br />
                                    <button type="button" className="aide-lien" onClick={() => voir(s)} title={`Ouvrir cet écran, l'aide ouverte (${s.ecran_cle})`}>{libelleEcran(s.module_cle, s.ecran_cle)}</button></td>
                                <td className="aide-admin-texte"><span className={'aide-nat ' + (NATURES[s.nature] ? s.nature : 'aide')}>{(NATURES[s.nature] || NATURES.aide).join(' ')}</span>{s.texte}</td>
                                <td>{s.courriel_etat === 'parti'
                                    ? <span className="aide-admin-ok"><i className="fa-solid fa-circle-check" /> parti</span>
                                    : <span className="aide-admin-ko"><i className="fa-solid fa-triangle-exclamation" /> non parti — {s.courriel_detail || 'raison inconnue'}</span>}</td>
                                <td>
                                    <select className={'docs-select aide-admin-suite ' + s.suite} value={s.suite} aria-label="Suite"
                                            onChange={(e) => donnerSuite(s, e.target.value, notes[s.id])}>
                                        {SUITES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    {s.suite !== 'nouveau' && (
                                        <input className="aide-admin-note" placeholder="Réponse au demandeur (facultative)" value={notes[s.id] || ''} aria-label="Réponse au demandeur"
                                               onChange={(e) => setNotes({ ...notes, [s.id]: e.target.value })}
                                               onBlur={() => { if ((notes[s.id] || '') !== (s.suite_note || '')) donnerSuite(s, s.suite, notes[s.id]); }} />
                                    )}
                                    {s.suite !== 'nouveau' && s.suite_par && <div className="hint">par {s.suite_par} le {momentFr(s.suite_le)}</div>}
                                </td>
                                <td className="aide-admin-rep"><CelluleReponse s={s} onRepondre={() => ouvrirReponse(s)} /></td>
                                <td><button type="button" className="aide-admin-suppr" title="Supprimer" aria-label="Supprimer" onClick={() => setASupprimer(s)}><i className="fa-solid fa-trash-can" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* v96 — « Répondre… » : récapitulatif modifiable, jamais d'envoi en un clic */}
            {reponse && (
                <div className="admin-modal-overlay" onClick={() => reponse.etat !== 'envoi' && setReponse(null)}>
                    <div className="admin-modal aide-admin-repf" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Répondre au demandeur">
                        <div className="admin-modal-h"><i className="fa-solid fa-reply" /> {reponse.s.reponse_etat ? 'Renvoyer la réponse à ' : 'Répondre à '}{reponse.s.auteur_nom}
                            <button className="x" onClick={() => reponse.etat !== 'envoi' && setReponse(null)} aria-label="Fermer">&times;</button></div>
                        {(reponse.etat === 'saisie' || reponse.etat === 'envoi') ? (<>
                            <div className="admin-modal-b aide-admin-repf-b">
                                <div className="aide-admin-repf-l"><b>À</b><span>{reponse.s.auteur_nom} &lt;{reponse.s.auteur_email}&gt;</span></div>
                                <div className="aide-admin-repf-l"><b>De</b><span>la boîte du département {reponse.s.departement_code}</span></div>
                                <div className="aide-admin-repf-l"><b>Objet</b><span>[Amélioration] Votre proposition — {reponse.s.suite === 'traite' ? 'traitée' : 'sans suite'}</span></div>
                                <div className="aide-admin-repf-l"><b>Message</b><span className="hint">prérempli, modifiable avant l'envoi</span></div>
                                <textarea value={reponse.texte} rows={12} maxLength={5000} aria-label="Message"
                                          onChange={(e) => setReponse({ ...reponse, texte: e.target.value })} disabled={reponse.etat === 'envoi'} />
                                <div className="mail-band info"><i className="fa-solid fa-circle-info" />
                                    <div>Le message part de la boîte du département, comme tous les courriels de l'application (journal, plafond d'envoi). Il n'est envoyé qu'au demandeur.</div></div>
                            </div>
                            <div className="admin-modal-f">
                                <button className="a-btn a-btn-secondary" onClick={() => setReponse(null)} disabled={reponse.etat === 'envoi'}>Annuler</button>
                                <button className="a-btn a-btn-primary" onClick={envoyerReponse} disabled={reponse.etat === 'envoi' || !reponse.texte.trim()}>
                                    {reponse.etat === 'envoi' ? <><i className="fa-solid fa-circle-notch fa-spin" /> Envoi…</> : <><i className="fa-solid fa-paper-plane" /> Envoyer la réponse</>}
                                </button>
                            </div>
                        </>) : (<>
                            <div className="admin-modal-b aide-admin-repf-fin">
                                {reponse.etat === 'transmise'
                                    ? <><i className="fa-solid fa-circle-check ok" /><b>Réponse transmise à {reponse.s.auteur_nom}.</b>
                                        <span className="hint">La colonne « Réponse au demandeur » en garde la date.</span></>
                                    : <><i className="fa-solid fa-triangle-exclamation ko" /><b>Réponse non transmise.</b>
                                        <span>{reponse.detail || 'raison inconnue'}</span>
                                        <span className="hint">La colonne le garde ; « Réessayer… » rouvre cette fenêtre.</span></>}
                            </div>
                            <div className="admin-modal-f"><button className="a-btn a-btn-primary" onClick={() => setReponse(null)}>Fermer</button></div>
                        </>)}
                    </div>
                </div>
            )}

            {aSupprimer && (
                <div className="admin-modal-overlay" onClick={() => setASupprimer(null)}>
                    <div className="admin-modal sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Supprimer cette proposition">
                        <div className="admin-modal-h"><i className="fa-solid fa-trash-can" style={{ color: '#b91c1c' }} /> Supprimer cette proposition ?
                            <button className="x" onClick={() => setASupprimer(null)} aria-label="Fermer">&times;</button></div>
                        <div className="admin-modal-b" style={{ fontSize: 13 }}>
                            <p style={{ margin: '0 0 8px' }}>« {aSupprimer.texte.length > 140 ? aSupprimer.texte.slice(0, 138) + '…' : aSupprimer.texte} »</p>
                            Le texte est effacé définitivement. Gardez-la si elle peut encore servir à améliorer l'application.
                        </div>
                        <div className="admin-modal-f">
                            <button className="a-btn a-btn-secondary" onClick={() => setASupprimer(null)}>Annuler</button>
                            <button className="a-btn a-btn-danger" onClick={supprimer}><i className="fa-solid fa-trash-can" /> Supprimer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminAide;
